import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  CheckCircleIcon, XCircleIcon, MoonIcon, AirplaneTiltIcon, ProhibitIcon, CalendarCheckIcon,
} from "@phosphor-icons/react";

const STATUSES = [
  { key: "present", label: "Present", icon: CheckCircleIcon, class: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  { key: "half_day", label: "Half-day", icon: MoonIcon, class: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" },
  { key: "paid_leave", label: "Paid leave", icon: AirplaneTiltIcon, class: "bg-secondary/15 text-secondary border-secondary/30" },
  { key: "unpaid_leave", label: "Unpaid leave", icon: ProhibitIcon, class: "bg-muted text-muted-foreground border-border" },
  { key: "absent", label: "Absent", icon: XCircleIcon, class: "bg-destructive/15 text-destructive border-destructive/30" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const [date, setDate] = useState(todayISO());
  const [users, setUsers] = useState([]);
  const [marks, setMarks] = useState({}); // user_id -> status
  const [monthSummary, setMonthSummary] = useState({}); // user_id -> counts

  const load = async () => {
    const [uRes, aRes] = await Promise.all([
      api.get("/users", { silent: true }).catch(() => ({ data: [] })),
      api.get("/attendance", { params: { date } }),
    ]);
    setUsers(uRes.data);
    const m = {};
    aRes.data.forEach((r) => { m[r.user_id] = r.status; });
    setMarks(m);

    const month = date.slice(0, 7);
    const monthRes = await api.get("/attendance", { params: { month } });
    const summary = {};
    monthRes.data.forEach((r) => {
      if (!summary[r.user_id]) summary[r.user_id] = { present: 0, half_day: 0, paid_leave: 0, unpaid_leave: 0, absent: 0 };
      if (summary[r.user_id][r.status] !== undefined) summary[r.user_id][r.status] += 1;
    });
    setMonthSummary(summary);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const set = (uid, status) => setMarks((m) => ({ ...m, [uid]: status }));

  const save = async () => {
    const payload = {
      date,
      marks: Object.entries(marks).map(([user_id, status]) => ({ user_id, status })),
    };
    if (payload.marks.length === 0) return toast.info("Nothing to save");
    try {
      const r = await api.post("/attendance/bulk", payload);
      toast.success(`Marked ${r.data.marked} record${r.data.marked === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const markAllPresent = () => {
    const m = {};
    users.forEach((u) => { m[u.id] = marks[u.id] || "present"; });
    setMarks(m);
  };

  const month = date.slice(0, 7);

  return (
    <div>
      <PageHeader
        overline="Team"
        title="Attendance"
        description="Mark daily attendance for every staff member. Absent and half-days automatically reduce the monthly payslip."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={markAllPresent} data-testid="attendance-mark-all-present">
              <CheckCircleIcon size={14} className="mr-1" /> Mark all present
            </Button>
            <Button onClick={save} data-testid="attendance-save-btn">
              <CalendarCheckIcon size={14} className="mr-1" /> Save
            </Button>
          </div>
        }
      />

      <Card className="p-6 mb-6">
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-48" data-testid="attendance-date" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Legend</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <span key={s.key} className={`px-2.5 py-1 rounded-md border text-xs flex items-center gap-1 ${s.class}`}>
                  <s.icon size={12} weight="duotone" /> {s.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="p-3 font-medium">Staff</th>
                <th className="p-3 font-medium">Role</th>
                <th className="p-3 font-medium text-center">Mark for {date}</th>
                <th className="p-3 font-medium text-right hidden md:table-cell">{month} totals (P · ½ · L · A)</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const cur = marks[u.id];
                const sum = monthSummary[u.id] || {};
                return (
                  <tr key={u.id} className="border-b border-border/50 last:border-b-0" data-testid={`attendance-row-${u.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7"><AvatarImage src={u.avatar} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-xs uppercase tracking-wider text-muted-foreground">{u.role?.replace("_", " ")}</td>
                    <td className="p-3">
                      <div className="flex justify-center gap-1 flex-wrap">
                        {STATUSES.map((s) => {
                          const active = cur === s.key;
                          return (
                            <button
                              key={s.key}
                              onClick={() => set(u.id, s.key)}
                              className={`px-2.5 py-1 rounded-md border text-xs flex items-center gap-1 transition-all ${active ? s.class + " shadow-sm scale-105" : "border-border hover:border-primary/50 text-muted-foreground"}`}
                              title={s.label}
                              data-testid={`attendance-mark-${u.id}-${s.key}`}
                            >
                              <s.icon size={12} weight={active ? "duotone" : "regular"} />
                              <span className="hidden lg:inline">{s.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="p-3 text-right text-xs text-muted-foreground font-mono-plex hidden md:table-cell">
                      {(sum.present || 0)} · {(sum.half_day || 0)} · {(sum.paid_leave || 0) + (sum.unpaid_leave || 0)} · {(sum.absent || 0)}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={4} className="p-10 text-center text-muted-foreground">No staff yet — add users in Staff & Access.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
